import React, { useState, useEffect } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { useAppContext } from '../../contexts/AppContext';
import QuizCollectionService from '../../services/quizCollection.service';
import AccessDenied from '../common/AccessDenied';

/**
 * Supports dual modes: tracking (for technologists) and preview (for supervisory roles)
 */
const QuizAccessGuard = ({ quizId, children, onAccessGranted, onAccessDenied }) => {
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const [error, setError] = useState(null);
  const { currentUser } = useAppContext();
  const history = useHistory();

  const quizCollectionService = new QuizCollectionService();

  useEffect(() => {
    checkAccess();
  }, [quizId, currentUser?.id]);

  const checkAccess = async () => {
    if (!currentUser?.id || !quizId) {
      setError('Missing user or quiz information');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await quizCollectionService.checkTechnologistAccess(quizId, currentUser.id);

      if (response.hasAccess) {
        setAccessInfo(response);

        onAccessGranted(response.mode);
      } else {
        setError(response.reason || 'You do not have access to this quiz collection.');

        onAccessDenied();
      }
    } catch (err) {
      console.error('Error checking quiz access:', err);

      setError('Failed to verify quiz access. Please try again.');
      onAccessDenied();
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.push('/quizzes');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Preparing quiz...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <AccessDenied title="Access Denied" message={error} showHelpText={currentUser?.role === 'technologist'} onGoBack={handleGoBack} reason={error} />;
  }

  if (!accessInfo?.hasAccess) {
    return <AccessDenied title="Access Required" message="You need an invitation to access this quiz collection." severity="warning" onGoBack={handleGoBack} />;
  }

  return <Box>{children}</Box>;
};

export default QuizAccessGuard;
